import { useMemo, useState } from "react";
import { 
  Alert, 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  TextField 
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  clearManualPaymentStep1Params,
  selectManualPaymentStep1Params,
  selectShowManualPaymentStep1,
  setManualPaymentStep2Params 
} from "./modalsSlice";
import { 
  extractManualDebtAmount, 
  normalizeManualPlateNumber, 
  resolveManualPaymentPlate 
} from "@/utils/manual-payment";
import { useCheckNumberPaymentMutation } from "@/pages/main/components/report/report-api";

const ManualPaymentModalStep1 = () => {
  const dispatch = useDispatch();
  const [plateNumberInputValue, setPlateNumberInputValue] = useState('');
  const { t } = useTranslation();
  const show = useSelector(selectShowManualPaymentStep1);
  const modalParams = useSelector(selectManualPaymentStep1Params);
  const [ inputErrorMessage, setInputErrorMessage ] = useState(undefined);
  const [ checkAlert, setCheckAlert ] = useState({
    severity: 'info',
    message: t('enter_number_to_continue'),
  });
  const normalizedPlateNumber = useMemo(() => (normalizeManualPlateNumber(
    plateNumberInputValue
  )), [plateNumberInputValue])
  const [manualCheckPayment, { 
    isLoading: manualCheckLoading,
    reset: resetManualCheckPayment,
  }] = useCheckNumberPaymentMutation();
  
  const handleCheck = async () => {
    if (!normalizedPlateNumber) {
      setInputErrorMessage(t('car_number_is_required'));
      return
    }

    try {
      const res = await manualCheckPayment({
        plateNumber: normalizedPlateNumber,
        buildingId: modalParams?.buildingId
      }).unwrap()

      const paymentAmount = extractManualDebtAmount(res)
      const paymentPlate = resolveManualPaymentPlate({
        inputPlate: normalizedPlateNumber,
        payload: res
      })

      if (Number.isFinite(paymentAmount) && paymentAmount > 0) {
        dispatch(setManualPaymentStep2Params({
          buildingId: modalParams?.buildingId,
          plateNumberPay: plateNumberInputValue,
          resolvedPaymentPlate: paymentPlate,
          resolvedPaymentAmount: paymentAmount,
        }))
        setPlateNumberInputValue(paymentPlate);
        handleCheckClose();
        // TODO Alert on next modal
        // onCheckAlert('success', `${t('debt_amount')}: ${amount} ${t('tenge')}`)
      } else {
        setCheckAlert({
          severity: 'check',
          message: paymentPlate + ' ' + t('no_debt'),
        });
      }

    } catch (err) {
      setCheckAlert({
        severity: 'error',
        message: err?.data?.message || t('error_occurred')
      });
    }
  }

  const handleCheckClose = () => {
    dispatch(clearManualPaymentStep1Params())
    setPlateNumberInputValue('');
    resetManualCheckPayment();
  }

  return (
    <Dialog 
      open={show} onClose={handleCheckClose}>
      <DialogTitle>{`${t('payment')} 1/2`}</DialogTitle>
      <DialogContent>
        <Alert 
          xs={{
            mb: 2,
          }}
          severity={checkAlert.severity}
        >
          {checkAlert.message}
        </Alert>
        <TextField
          onChange={({ target }) => { 
            setPlateNumberInputValue(target.value);
            setInputErrorMessage(undefined);
          }}
          value={plateNumberInputValue}
          autoFocus
          margin="dense"
          id="govNumberPay"
          label={t('license_plate')}
          fullWidth
          disabled={manualCheckLoading}
          error={inputErrorMessage}
          onError={inputErrorMessage}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCheckClose} color="error">
          {t('close')}
        </Button>
        <Button
          loading={manualCheckLoading}
          disabled={!normalizedPlateNumber || manualCheckLoading}
          onClick={handleCheck}
        >
          {t('send')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ManualPaymentModalStep1;